import { useEffect, useState } from 'react';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Alert, AlertTitle } from '@material-ui/lab';
import { catalogApiRef, CatalogApi } from '@backstage/plugin-catalog-react';
import {
  IdentityApi,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import Grid from '@material-ui/core/Grid';
import { Entity } from '@backstage/catalog-model';
import useAsync from 'react-use/lib/useAsync';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      '& > * + *': {
        marginTop: theme.spacing(2),
      },
    },
  }),
);

// Custom hook to get logged-in user groups
const useLoggedInUserGroups = (
  identityApi: IdentityApi,
  catalogApi: CatalogApi,
) => {
  const { value: groups, error } = useAsync(async () => {
    const profile = await identityApi.getBackstageIdentity();
    const response = await catalogApi.getEntities({
      filter: [{ kind: 'group', 'relations.hasMember': profile.userEntityRef }],
      fields: ['metadata', 'kind'],
    });
    return response.items || [];
  }, [identityApi, catalogApi]);

  if (error) {
    // TODO: Log Error: console.error('Error fetching groups:', error);
    return [];
  }

  return groups;
};

// Custom hook to check if the user is valid in the catalog
const useValidCatalogUser = (
  identityApi: IdentityApi,
  catalogApi: CatalogApi,
) => {
  const { value: isValid, error } = useAsync(async () => {
    const profile = await identityApi.getBackstageIdentity();
    const { items } = await catalogApi.getEntitiesByRefs({
      entityRefs: [profile.userEntityRef],
    });
    return (items ?? []).length > 0;
  }, [identityApi, catalogApi]);

  if (error) {
    // TODO: Log Error:  console.error('Error checking catalog user:', error);
    return false;
  }

  return isValid;
};

const isMemberOfGroup = (
  groups: Entity[] | undefined,
  excludedGroupName: string,
): boolean => {
  return (groups ?? []).some(
    group => group.metadata.name !== excludedGroupName,
  );
};

export default function AnnouncementBanner() {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const catalogApi: CatalogApi = useApi(catalogApiRef);

  const validUser = useValidCatalogUser(identityApi, catalogApi);
  const userGroups = useLoggedInUserGroups(identityApi, catalogApi);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!validUser) {
      setIsVisible(false);
      return;
    }

    if (
      userGroups &&
      isMemberOfGroup(userGroups, 'sg_Users_Products&Engineering')
    ) {
      setIsVisible(false);
      return;
    }

    setIsVisible(true);
  }, [validUser, userGroups]);

  if (!isVisible) {
    return null;
  }

  return (
    <Grid item xs={12} md={4}>
      <div className={classes.root}>
        <Alert variant="outlined" severity="warning">
          <AlertTitle>
            <strong>Announcement</strong>
          </AlertTitle>
          <strong>
            You are not in a squad. Click the button below to join a squad and
            partake in squad activities.
          </strong>
          <CardActions style={{ justifyContent: 'center', marginTop: '10px' }}>
            <Button
              size="medium"
              variant="contained"
              color="success"
              href="/docs/default/location/core-catalog/how-to/01-add-your-squad/"
              target="_blank"
            >
              Join Squad
            </Button>
          </CardActions>
        </Alert>
      </div>
    </Grid>
  );
}
